/**
 * Utility class for loading external scripts and stylesheets.
 */
export default class ResourceLoader {
  private static scriptCache: { [key: string]: Promise<void> } = {};
  private static cssCache: { [key: string]: Promise<void> } = {};

  /**
   * Loads a script from the given URL.
   * 
   * @param {string} url - The URL of the script to load.
   * @returns {Promise<void>} A promise that resolves when the script is loaded.
   */
  public static loadJs(url: string): Promise<void> {
    // Check if the script is already being loaded or has been loaded
    if (ResourceLoader.scriptCache[url]) {
      //console.info('ResourceLoader - JS Promise exists, waiting', url);
      return ResourceLoader.scriptCache[url]; // Return the existing promise
    }

    // Create a new promise for loading the script
    const scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;

      script.onload = () => {
        //console.info('ResourceLoader - Loaded JS', url);
        resolve();
      };

      script.onerror = () => {
        reject(new Error(`ResourceLoader - Failed to load script: ${url}`));
      };

      document.head.appendChild(script);
    });

    // Cache the promise
    ResourceLoader.scriptCache[url] = scriptPromise;

    return scriptPromise;
  }

  /**
   * Loads a CSS stylesheet from the given URL.
   * 
   * @param {string} url - The URL of the stylesheet to load.
   * @param {Record<string, any>} [options] - Optional settings for loading the stylesheet.
   * @returns {Promise<void>} A promise that resolves when the stylesheet is loaded.
   */
  public static loadCss(url: string, options?: Record<string, any>): Promise<void> {
    // Check if the stylesheet is already being loaded or has been loaded
    if (ResourceLoader.cssCache[url]) {
      //console.info('ResourceLoader - CSS Promise exists, waiting', url);
      return ResourceLoader.cssCache[url]; // Return the existing promise
    }

    // Create a new promise for loading the stylesheet
    const cssPromise = new Promise<void>((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;

      if (options && options.attributes) {
        for (const key in options.attributes) {
          if (options.attributes.hasOwnProperty(key)) {
            link.setAttribute(key, options.attributes[key]);
          }
        }
      }

      link.onload = () => {
        //console.info('ResourceLoader - Loaded CSS', url);
        resolve();
      };

      link.onerror = () => {
        reject(new Error(`ResourceLoader - Failed to load stylesheet: ${url}`));
      };

      document.head.appendChild(link);
    });

    // Cache the promise
    ResourceLoader.cssCache[url] = cssPromise;

    return cssPromise;
  }
}
